import { useCallback, useEffect, useState } from "react";
import { type ApolloClient } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { captureException } from "@sentry/react";
import QuickLRU from "quick-lru";
import { dmsDocumentsUrlQuery } from "@domain/context/queries/dmsDocumentUrl.graphql";

// Re-mint 30s before the URL actually expires - a short safety margin so
// an in-flight pdfjs fetch never races the expiry window. The backend
// currently signs for 5 minutes; if that constant changes the margin
// still holds because it's relative to the server-reported `expiresAt`.
const URL_REFRESH_MARGIN_MS = 30_000;

// 2000 entries × ~200 bytes = ~400 KB upper bound on cache memory. Sized
// for "open the whole DMS grid + a few re-opens" workflows without ever
// thrashing. Each cache hit AVOIDS one `dmsDocumentAccessLog` row, so
// the audit trail only captures "first open per 5-minute window" - an
// explicit tradeoff for UX (no spinner spam when the same PDF reopens)
// and resolver load (no hammering when N components render the same
// doc).
const CACHE_MAX_ENTRIES = 2000;

type CacheEntry = { url: string; expiresAt: number };
const urlCache = new QuickLRU<string, CacheEntry>({ maxSize: CACHE_MAX_ENTRIES });

// Lazy, once-per-page-lifetime subscription to Firebase auth state. On
// any transition to "no user" (logout, token revoked, account deleted)
// we drop the URL cache so a URL minted under user A can't survive into
// user B's session on the same device. This is hygiene, not a security
// boundary - the actual guard is server-side `assertCanRead`. Hence
// "better than nothing": if Firebase isn't ready (tests, SSR) we
// silently skip, and the cache simply lives until next page reload.
let authListenerRegistered = false;
const ensureAuthListener = () => {
    if (authListenerRegistered) {
        return;
    }
    authListenerRegistered = true;
    void (async () => {
        try {
            const [{ auth }, { onAuthStateChanged }] = await Promise.all([
                import("@domain/api/firebase/firebase"),
                import("firebase/auth"),
            ]);
            onAuthStateChanged(auth, (user) => {
                if (!user) {
                    urlCache.clear();
                }
            });
        } catch {
            // Firebase not initialised - skip; cache survives until reload.
        }
    })();
};

const cacheKey = (documentId: string, companyId: string) => `${companyId}:${documentId}`;

// An entry is reusable iff its server-reported expiry is at least
// `URL_REFRESH_MARGIN_MS` in the future. Treat anything closer as
// expired so a fresh mint is forced before pdfjs can possibly outrun the
// URL.
const isFresh = (entry: CacheEntry) => entry.expiresAt * 1000 - Date.now() > URL_REFRESH_MARGIN_MS;

export type DmsDocumentUrlResult = {
    url: string | undefined;
    expiresAt: number | undefined;
    loading: boolean;
    // `null` until a fetch fails. Lets viewers distinguish three states:
    // loading (loading=true), success (url defined), and broken (error
    // defined, url undefined). Without it, `!url && !loading` is
    // ambiguous and the UI defaults to a perpetual spinner.
    error: Error | null;
    // Caller-driven retry. Cheap to call (idempotent - runs the same
    // cache-aware fetch as mount). Use it to power a "try again" button
    // in the failure UI rather than asking the user to navigate away
    // and back. Stable identity across renders.
    retry: () => void;
};

/**
 * Bulk-fetch signed URLs for a list of document ids. Cache-first: only
 * misses hit the resolver. Returns a map keyed on documentId. Missing
 * docs (unknown id or permission-stripped) are simply absent from the
 * map. The backend writes one audit log row per cache miss; cached hits
 * are silent (the previous mint within this 5-min window already
 * produced an audit row).
 */
export async function fetchDmsDocumentsUrl(
    client: ApolloClient,
    documentIds: string[],
    companyId: string,
): Promise<Map<string, CacheEntry>> {
    const out = new Map<string, CacheEntry>();
    if (documentIds.length === 0) {
        return out;
    }
    const misses: string[] = [];
    for (const id of documentIds) {
        const cached = urlCache.get(cacheKey(id, companyId));
        if (cached && isFresh(cached)) {
            out.set(id, cached);
        } else {
            misses.push(id);
        }
    }
    if (misses.length === 0) {
        return out;
    }
    const res = await client.query({
        query: dmsDocumentsUrlQuery,
        variables: { input: { ids: misses, companyId } },
        fetchPolicy: "no-cache",
    });
    for (const payload of res.data?.dmsDocumentsUrl ?? []) {
        if (!payload?.url || payload.expiresAt == null) {
            continue;
        }
        const entry: CacheEntry = { url: payload.url, expiresAt: payload.expiresAt };
        urlCache.set(cacheKey(payload.id, companyId), entry);
        out.set(payload.id, entry);
    }
    return out;
}

/**
 * Single-document convenience over the bulk resolver. Backed by the same
 * LRU, so a viewer that asks for one doc shares the cache with anything
 * that asked for it in a batch.
 */
export async function fetchDmsDocumentUrl(
    client: ApolloClient,
    documentId: string,
    companyId: string,
): Promise<CacheEntry | null> {
    const map = await fetchDmsDocumentsUrl(client, [documentId], companyId);
    return map.get(documentId) ?? null;
}

type InternalState = Omit<DmsDocumentUrlResult, "retry">;
const EMPTY_STATE: InternalState = { url: undefined, expiresAt: undefined, loading: false, error: null };

/**
 * React-friendly variant. Fires on mount / id-change (cache-aware) and
 * schedules a refetch ~30s before the URL expires so long-lived viewers
 * (`PdfSingleDocumentViewer`) never see a 403.
 *
 * On error: sets `error` (NOT a perpetual spinner) and captures to
 * Sentry. Callers should branch on `error` to render a recoverable
 * failure UI and wire a retry button to the returned `retry()`.
 */
export function useDmsDocumentUrl(
    documentId: string | null | undefined,
    companyId: string | null | undefined,
): DmsDocumentUrlResult {
    ensureAuthListener();
    const client = useApolloClient();
    const skip = !documentId || !companyId;
    const [state, setState] = useState<InternalState>(() =>
        skip ? EMPTY_STATE : { url: undefined, expiresAt: undefined, loading: true, error: null },
    );
    // Bumped by `retry()` to force the effect to re-run on demand.
    const [retryNonce, setRetryNonce] = useState(0);
    const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

    useEffect(() => {
        if (skip) {
            return undefined;
        }
        let cancelled = false;
        let refreshTimer: ReturnType<typeof setTimeout> | undefined;

        const refresh = async () => {
            try {
                const entry = await fetchDmsDocumentUrl(client, documentId, companyId);
                if (cancelled) {
                    return;
                }
                if (!entry) {
                    // Resolver returned no payload for this id (NOT_FOUND
                    // or permission-stripped). Surface as an error so the
                    // UI doesn't sit on a spinner forever.
                    const e = new Error(`No URL returned for document ${documentId}`);
                    captureException(e, { extra: { documentId, companyId } });
                    setState({ url: undefined, expiresAt: undefined, loading: false, error: e });
                    return;
                }
                setState({ url: entry.url, expiresAt: entry.expiresAt, loading: false, error: null });
                const msUntilRefresh = entry.expiresAt * 1000 - Date.now() - URL_REFRESH_MARGIN_MS;
                refreshTimer = setTimeout(() => void refresh(), Math.max(msUntilRefresh, 1_000));
            } catch (e) {
                if (cancelled) {
                    return;
                }
                const err = e instanceof Error ? e : new Error(String(e));
                captureException(err, { extra: { documentId, companyId } });
                setState({ url: undefined, expiresAt: undefined, loading: false, error: err });
            }
        };

        void refresh();
        return () => {
            cancelled = true;
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
        };
    }, [client, documentId, companyId, skip, retryNonce]);

    return skip ? { ...EMPTY_STATE, retry } : { ...state, retry };
}
